import React, { useEffect, useRef, useCallback, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { getAllLanguage } from "../../../../store/language/actions";
import { toast } from "react-toastify";
import FadeLoader from "react-spinners/FadeLoader";
import { useNavigate, useParams } from "react-router-dom";
import {
  editCharacter,
  uploadFile,
  viewCharacter,
} from "../../../../store/actions";

// ─── Personality Trait Slider Component ──────────────────────────────────────
const TraitSlider = ({ trait, index, onChange, onRemove, canRemove }) => {
  const trackRef = useRef(null);
  const isDragging = useRef(false);

  const getValueFromEvent = useCallback(
    (e) => {
      const track = trackRef.current;
      if (!track) return trait.value;
      const rect = track.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const ratio = Math.max(
        0,
        Math.min(1, (clientX - rect.left) / rect.width),
      );
      return Math.round(ratio * 100);
    },
    [trait.value],
  );

  const handleMouseDown = (e) => {
    e.preventDefault();
    isDragging.current = true;
    onChange(index, "value", getValueFromEvent(e));
    const handleMouseMove = (e) => {
      if (!isDragging.current) return;
      onChange(index, "value", getValueFromEvent(e));
    };
    const handleMouseUp = () => {
      isDragging.current = false;
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleTouchStart = (e) => {
    isDragging.current = true;
    onChange(index, "value", getValueFromEvent(e));
    const handleTouchMove = (e) => {
      if (!isDragging.current) return;
      e.preventDefault();
      onChange(index, "value", getValueFromEvent(e));
    };
    const handleTouchEnd = () => {
      isDragging.current = false;
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);
  };

  const pct = trait.value ?? 50;
  // Brand colors: #000 (primary) → #c8bbe8 (secondary)
  // Interpolate from secondary to primary based on value
  const thumbColor =
    pct < 50
      ? `color-mix(in srgb, #c8bbe8 ${100 - pct * 2}%, #000 ${pct * 2}%)`
      : `color-mix(in srgb, #000 ${(pct - 50) * 2}%, #c8bbe8 ${(100 - pct) * 2}%)`;
  const fillColor = thumbColor;

  return (
    <div
      style={{
        background: "#fff",
        border: "1.5px solid #e2e8f0",
        borderRadius: 12,
        padding: "14px 16px",
        marginBottom: 10,
        transition: "border-color 0.2s, box-shadow 0.2s",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: "1px",
            color: "#94a3b8",
          }}
        >
          Trait {index + 1}
        </span>
        {canRemove && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            title="Remove trait"
            style={{
              background: "none",
              border: "none",
              width: 26,
              height: 26,
              borderRadius: 6,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#cbd5e1",
              cursor: "pointer",
              padding: 0,
              fontSize: 16,
              lineHeight: 1,
              transition: "background 0.15s, color 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#fee2e2";
              e.currentTarget.style.color = "#ef4444";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "none";
              e.currentTarget.style.color = "#cbd5e1";
            }}
          >
            ×
          </button>
        )}
      </div>

      {/* Start / End label inputs */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <span
            style={{
              display: "inline-block",
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: "1.2px",
              padding: "2px 7px",
              borderRadius: 4,
              background: "#dcfce7",
              color: "#15803d",
              marginBottom: 5,
            }}
          >
            START
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="e.g. Introverted"
            value={trait.startLabel}
            onChange={(e) => onChange(index, "startLabel", e.target.value)}
            style={{ fontSize: 13 }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <span
            style={{
              display: "inline-block",
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: "1.2px",
              padding: "2px 7px",
              borderRadius: 4,
              background: "#fef3c7",
              color: "#b45309",
              marginBottom: 5,
            }}
          >
            END
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="e.g. Extroverted"
            value={trait.endLabel}
            onChange={(e) => onChange(index, "endLabel", e.target.value)}
            style={{ fontSize: 13, textAlign: "right" }}
          />
        </div>
      </div>

      {/* Slider Track */}
      <div style={{ userSelect: "none" }}>
        <div
          ref={trackRef}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          style={{
            position: "relative",
            height: 8,
            background: "#e2e8f0",
            borderRadius: 99,
            cursor: "grab",
            margin: "0 10px",
            touchAction: "none",
          }}
        >
          {/* Fill */}
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              height: "100%",
              width: `${pct}%`,
              borderRadius: 99,
              background: `linear-gradient(90deg, #c8bbe8, #000)`,
              pointerEvents: "none",
              transition: "width 0.04s",
            }}
          />
          {/* Thumb */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: `${pct}%`,
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: thumbColor,
              border: "3px solid #fff",
              boxShadow: `0 0 0 3px ${thumbColor}33, 0 2px 8px rgba(0,0,0,0.15)`,
              transform: "translate(-50%, -50%)",
              pointerEvents: "none",
              transition: "background 0.2s, box-shadow 0.2s",
            }}
          />
        </div>

        {/* Meta row: start label — value pill — end label */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: 10,
            padding: "0 4px",
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "#c8bbe8",
              maxWidth: 110,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {trait.startLabel || "Start"}
          </span>
          <span
            style={{
              background: thumbColor,
              color: "#fff",
              fontSize: 11,
              fontWeight: 800,
              padding: "3px 10px",
              borderRadius: 20,
              minWidth: 42,
              textAlign: "center",
              transition: "background 0.2s",
            }}
          >
            {pct}%
          </span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: `#000`,
              maxWidth: 110,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              textAlign: "right",
            }}
          >
            {trait.endLabel || "End"}
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── Personality Traits Panel ─────────────────────────────────────────────────
const PersonalityTraitsPanel = ({
  optionIndex,
  optionName,
  traits,
  onChange,
}) => {
  const addTrait = () => {
    onChange([...traits, { startLabel: "", endLabel: "", value: 50 }]);
  };

  const removeTrait = (i) => {
    onChange(traits.filter((_, idx) => idx !== i));
  };

  const handleTraitChange = (i, field, value) => {
    const updated = traits.map((t, idx) =>
      idx === i ? { ...t, [field]: value } : t,
    );
    onChange(updated);
  };

  return (
    <div
      style={{
        border: "1.5px solid #f0a8d0",
        borderRadius: 14,
        overflow: "hidden",
        marginTop: 16,
      }}
    >
      {/* Panel Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          background: "linear-gradient(135deg, #fff0f7 0%, #f8f5ff 100%)",
          borderBottom: "1.5px solid #f0d6eb",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 32,
              height: 32,
              background: "linear-gradient(135deg, #000, #c8bbe8)",
              borderRadius: 9,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: 15,
            }}
          >
            🎭
          </div>
          <div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "#0f172a",
                lineHeight: 1.2,
              }}
            >
              Personality Traits
            </div>
            <div style={{ fontSize: 11, color: "#64748b" }}>
              {optionName
                ? `Option: ${optionName}`
                : `Option ${optionIndex + 1}`}
            </div>
          </div>
        </div>
        <span
          style={{
            background: "#ffe0f1",
            color: "#000",
            fontSize: 11,
            fontWeight: 700,
            padding: "3px 10px",
            borderRadius: 20,
          }}
        >
          {traits.length} trait{traits.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Traits List */}
      <div style={{ padding: traits.length > 0 ? "12px 14px 4px" : 0 }}>
        {traits.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "28px 20px",
              color: "#94a3b8",
            }}
          >
            <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.5 }}>
              🎛️
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#64748b" }}>
              No traits yet
            </div>
            <div style={{ fontSize: 12, marginTop: 3 }}>
              Add traits to define this personality's spectrum
            </div>
          </div>
        ) : (
          traits.map((trait, i) => (
            <TraitSlider
              key={i}
              trait={trait}
              index={i}
              onChange={handleTraitChange}
              onRemove={removeTrait}
              canRemove={traits.length > 1}
            />
          ))
        )}
      </div>

      {/* Add Trait Button */}
      <div style={{ padding: "8px 14px 14px" }}>
        <button
          type="button"
          onClick={addTrait}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 7,
            width: "100%",
            padding: "9px",
            border: "1.5px dashed #f0a8d0",
            borderRadius: 10,
            background: "#fff0f7",
            color: "#000",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.2s",
            outline: "none",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#ffe0f1";
            e.currentTarget.style.borderColor = "#000";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#fff0f7";
            e.currentTarget.style.borderColor = "#f0a8d0";
          }}
        >
          <span style={{ fontSize: 18, lineHeight: 1, marginTop: -1 }}>+</span>
          Add Trait
        </button>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const EditCharacterType = () => {
  const { CHARACTER, loading } = useSelector((state) => state.Character);
  const { id } = useParams();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState();
  const [language, setLanguage] = useState([]);
  const navigate = useNavigate();
  const [optionImageLoading, setOptionImageLoading] = useState({});
  const [optionVideoLoading, setOptionVideoLoading] = useState({});
  const [optionAudioLoading, setOptionAudioLoading] = useState({});
  const [isDisabled, setIsDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    type: "",
    name: "",
    styleType: "",
    gender: "",
    titles: [
      {
        language: "",
        title: "",
      },
    ],
    options: [
      {
        name: "",
        image: "",
        video: "",
        audio: "",
        traits: [], // ✅ NEW: personality traits per option
        titles: [
          {
            language: "",
            title: "",
          },
        ],
      },
    ],
  });

  useEffect(() => {
    if (!CHARACTER?.data) {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      id: CHARACTER.data._id,
      type: CHARACTER.data.type || "",
      name: CHARACTER.data.name || "",
      styleType: CHARACTER.data.styleType || "",
      gender: CHARACTER.data.gender || "",
      titles: CHARACTER.data.titles?.map((item) => ({
        language: item?.language || "",
        title: item?.title || "",
      })) || [{ language: "", title: "" }],
      options: CHARACTER.data.options?.map((item) => ({
        name: item?.name || "",
        image: item?.image || "",
        video: item?.video || "",
        audio: item?.audio || "",
        traits: item?.traits || [], // ✅ NEW: load traits from API
        titles: item?.titles?.map((title) => ({
          language: title?.language || "",
          title: title?.title || "",
        })) || [{ language: "", title: "" }],
      })) || [
        {
          name: "",
          image: "",
          video: "",
          audio: "",
          traits: [],
          titles: [{ language: "", title: "" }],
        },
      ],
    }));
  }, [CHARACTER]);

  const fetchLanguages = async () => {
    try {
      const callback = (err, res) => {
        if (err) {
          toast.error(err);
          console.error(err);
        } else {
          setLanguage(res);
        }
      };
      dispatch(getAllLanguage({ callback }));
    } catch (error) {
      console.error("Error fetching languages:", error);
    }
  };

  useEffect(() => {
    fetchLanguages();
  }, []);

  const validateFormData = () => {
    const newErrors = {};

    if (!formData.type) newErrors.type = "Type is required";
    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.gender) newErrors.gender = "Gender is required";
    if (!formData.styleType) newErrors.styleType = "Style Type is required";

    formData.titles.forEach((title, index) => {
      if (!title.language)
        newErrors[`titles-${index}-language`] = "Language is required";
      if (!title.title)
        newErrors[`titles-${index}-title`] = "Title is required";
    });

    formData.options.forEach((option, optionIndex) => {
      if (!option.name)
        newErrors[`options-${optionIndex}-name`] = "Option name is required";

      option.titles.forEach((title, titleIndex) => {
        if (!title.language)
          newErrors[`options-${optionIndex}-titles-${titleIndex}-language`] =
            "Language is required";
        if (!title.title)
          newErrors[`options-${optionIndex}-titles-${titleIndex}-title`] =
            "Title is required";
      });
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fetchData = async () => {
    try {
      const callback = (err, res) => {
        if (err) {
          toast.error(err);
          console.error(err);
        } else {
          console.log(res, "fetchData");
        }
      };
      dispatch(viewCharacter({ data: id, callback }));
    } catch (error) {
      console.error("Error fetching Data:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
    setErrors({ ...errors, [name]: "" });
  };

  const handleTitleTypeChange = (index, field, value) => {
    const updatedTitles = [...formData.titles];
    updatedTitles[index] = { ...updatedTitles[index], [field]: value };
    setFormData({ ...formData, titles: updatedTitles });
    setErrors((prev) => ({ ...prev, [`titles-${index}-${field}`]: "" }));
  };

  const addTitleType = () => {
    setFormData({
      ...formData,
      titles: [...formData.titles, { language: "", title: "" }],
    });
  };

  const removeTitleType = (index) => {
    const updatedTitles = formData.titles.filter((_, i) => i !== index);
    setFormData({ ...formData, titles: updatedTitles });
  };

  const handleOptionChange = (optionIndex, field, value) => {
    const updatedOptions = [...formData.options];
    updatedOptions[optionIndex] = {
      ...updatedOptions[optionIndex],
      [field]: value,
    };
    setFormData({ ...formData, options: updatedOptions });
    setErrors((prev) => ({ ...prev, [`options-${optionIndex}-${field}`]: "" }));
  };

  const handleOptionTitleChange = (optionIndex, titleIndex, field, value) => {
    const updatedOptions = [...formData.options];
    const updatedTitles = [...updatedOptions[optionIndex].titles];
    updatedTitles[titleIndex] = {
      ...updatedTitles[titleIndex],
      [field]: value,
    };
    updatedOptions[optionIndex] = {
      ...updatedOptions[optionIndex],
      titles: updatedTitles,
    };
    setFormData({ ...formData, options: updatedOptions });
    setErrors((prev) => ({
      ...prev,
      [`options-${optionIndex}-titles-${titleIndex}-${field}`]: "",
    }));
  };

  const addOptionTitle = (optionIndex) => {
    const updatedOptions = [...formData.options];
    updatedOptions[optionIndex].titles.push({ language: "", title: "" });
    setFormData({ ...formData, options: updatedOptions });
  };

  const removeOptionTitle = (optionIndex, titleIndex) => {
    const updatedOptions = [...formData.options];
    updatedOptions[optionIndex].titles = updatedOptions[
      optionIndex
    ].titles.filter((_, i) => i !== titleIndex);
    setFormData({ ...formData, options: updatedOptions });
  };

  const addOption = () => {
    setFormData({
      ...formData,
      options: [
        ...formData.options,
        {
          name: "",
          image: "",
          video: "",
          audio: "",
          traits: [], // ✅ NEW: include traits in new option
          titles: [{ language: "", title: "" }],
        },
      ],
    });
  };

  const removeOption = (optionIndex) => {
    const updatedOptions = formData.options.filter((_, i) => i !== optionIndex);
    setFormData({ ...formData, options: updatedOptions });
  };

  const handleOptionImageFiles = (optionIndex, _files) => {
    const files = Array.from(_files).filter((file) => file.size < 10e6);
    if (files.length < _files.length) {
      toast.error("Max file upload size is 10MB");
      return;
    }
    setOptionImageLoading((prev) => ({ ...prev, [optionIndex]: true }));
    const callback = (err, res) => {
      if (err) {
        toast.error("Failed to upload file");
      } else {
        toast.success("File uploaded successfully");
        handleOptionChange(optionIndex, "image", res?.data?.url);
      }
      setOptionImageLoading((prev) => ({ ...prev, [optionIndex]: false }));
    };
    dispatch(uploadFile({ data: files[0], callback }));
  };

  const handleOptionVideoFiles = (optionIndex, _files) => {
    const MAX_FILE_SIZE = 100 * 1024 * 1024;
    const files = Array.from(_files).filter(
      (file) => file.size <= MAX_FILE_SIZE,
    );
    if (files.length < _files.length) {
      toast.error("Max file upload size is 100MB");
      return;
    }
    setOptionVideoLoading((prev) => ({ ...prev, [optionIndex]: true }));
    const callback = (err, res) => {
      if (err) {
        toast.error("Failed to upload video");
      } else {
        toast.success("Video uploaded successfully");
        handleOptionChange(optionIndex, "video", res?.data?.url);
      }
      setOptionVideoLoading((prev) => ({ ...prev, [optionIndex]: false }));
    };
    dispatch(uploadFile({ data: files[0], callback }));
  };

  const handleOptionAudioFiles = (optionIndex, _files) => {
    const MAX_FILE_SIZE = 50 * 1024 * 1024;
    const files = Array.from(_files).filter(
      (file) => file.size <= MAX_FILE_SIZE,
    );
    if (files.length < _files.length) {
      toast.error("Max file upload size is 50MB");
      return;
    }

    const updatedOptions = [...formData.options];
    updatedOptions[optionIndex].audio = URL.createObjectURL(files[0]);
    setFormData({ ...formData, options: updatedOptions });

    setOptionAudioLoading((prev) => ({ ...prev, [optionIndex]: true }));

    const callback = (err, res) => {
      if (err) {
        toast.error("Failed to upload audio");
      } else {
        toast.success(res.message);
        const updatedOptions = [...formData.options];
        updatedOptions[optionIndex].audio = res?.data?.url;
        setFormData((prev) => ({ ...prev, options: updatedOptions }));
      }
      setOptionAudioLoading((prev) => ({ ...prev, [optionIndex]: false }));
    };
    dispatch(uploadFile({ data: files[0], callback }));
  };

  const handleFiles = (_files) => {
    const files = Array.from(_files).filter((file) => file.size < 10e6);
    if (files.length < _files.length) {
      toast.error("Max file upload size is 10MB");
      return;
    }
    setIsLoading(true);
    const callback = (err, res) => {
      if (err) {
        toast.error("Failed to upload file");
      } else {
        toast.success("File uploaded successfully");
        setFormData((prev) => ({ ...prev, image: res?.data?.url }));
      }
      setIsLoading(false);
    };
    dispatch(uploadFile({ data: files[0], callback }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsDisabled(true);

    if (!validateFormData()) {
      setIsDisabled(false);
      return;
    }

    try {
      const callback = (err, res) => {
        if (err) {
          toast.error(err);
          console.error("Error submitting:", err);
        } else {
          toast.success("Character Type Updated Successfully");
          navigate("/character-setting");
          setFormData({
            type: "",
            name: "",
            gender: "",
            titles: [{ language: "", title: "" }],
            options: [
              {
                name: "",
                image: "",
                video: "",
                audio: "",
                traits: [],
                titles: [{ language: "", title: "" }],
              },
            ],
          });
        }
        setIsDisabled(false);
      };
      dispatch(editCharacter({ data: formData, callback }));
    } catch (error) {
      console.error("Error during submission:", error);
      setIsDisabled(false);
    }
  };

  return (
    <>
      {loading && (
        <div className="main-loader">
          <FadeLoader size={1000} />
        </div>
      )}
      <section className="addForm position-relative py-3">
        <Container>
          <Row>
            <Col lg="12" className="my-2">
              <div className="cardCstm p-3 rounded bg-white">
                <Form className="mx-auto" onSubmit={handleSubmit}>
                  <div className="py-3">
                    <div className="topHead pb-2">
                      <h5 className="m-0 fw-sbold" style={{ color: "#2E3A59" }}>
                        Edit Modal:
                      </h5>
                    </div>
                    <div className="mx-auto" style={{ maxWidth: 777 }}>
                      <Row className="justify-content-start">
                        <Col md="6" className="my-2">
                          <label
                            htmlFor="ModalType"
                            className="form-label text-dark m-0 pb-1 fw-sbold text-uppercase"
                          >
                            Modal Type
                          </label>
                          <select
                            className="form-control"
                            name="type"
                            value={formData.type}
                            onChange={handleChange}
                          >
                            <option value="">Select Modal type</option>
                            <option value="Age">Age</option>
                            <option value="EthnicBackground">
                              Ethnic background
                            </option>
                            <option value="Height">Height</option>
                            <option value="Penis">Penis</option>
                            <option value="Voice">Voices</option>
                            <option value="Physique">Physique</option>
                            <option value="Breast">Breast/Chest</option>
                            <option value="Butt">Butt</option>
                            <option value="HairStyle">Hair Style</option>
                            <option value="HairColor">Hair Color</option>
                            <option value="Eye">Eye</option>
                            <option value="Personality">Personality</option>
                            <option value="SexualOrientation">
                              Sexual Orientation
                            </option>
                            <option value="ChatStyle">Chat Style</option>
                            <option value="Occupation">Occupation</option>
                            <option value="StartingRelationship">
                              Starting Relationship
                            </option>
                            <option value="kinks">Kinks</option>
                          </select>
                          {errors.type && (
                            <small className="text-danger">{errors.type}</small>
                          )}
                        </Col>
                        <Col md="6" className="my-2">
                          <label
                            htmlFor="name"
                            className="form-label text-dark m-0 pb-1 fw-sbold text-uppercase"
                          >
                            Name
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                          />
                          {errors.name && (
                            <small className="text-danger">{errors.name}</small>
                          )}
                        </Col>
                        <Col md="6" className="my-2">
                          <label
                            htmlFor="Gender"
                            className="form-label text-dark m-0 pb-1 fw-sbold text-uppercase"
                          >
                            Gender
                          </label>
                          <select
                            className="form-control"
                            name="gender"
                            value={formData.gender}
                            onChange={handleChange}
                          >
                            <option value="">Select Gender</option>
                            <option value="all">All</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                          </select>
                          {errors.gender && (
                            <small className="text-danger">
                              {errors.gender}
                            </small>
                          )}
                        </Col>
                        <Col md="6" className="my-2">
                          <label
                            htmlFor="ModalType"
                            className="form-label text-dark m-0 pb-1 fw-sbold text-uppercase"
                          >
                            Style type
                          </label>
                          <select
                            className="form-control"
                            name="styleType"
                            value={formData.styleType}
                            onChange={handleChange}
                          >
                            <option value="">Select Style type</option>
                            <option value="Realistic">Realistic</option>
                            <option value="Anime">Anime</option>
                          </select>
                          {errors.styleType && (
                            <small className="text-danger">
                              {errors.styleType}
                            </small>
                          )}
                        </Col>

                        {/* Titles Section */}
                        {formData?.titles?.map((item, index) => (
                          <React.Fragment key={index}>
                            <Col md="6" className="my-2">
                              <label
                                htmlFor={`language-${index}`}
                                className="form-label text-dark m-0 pb-1 fw-sbold text-uppercase"
                              >
                                Select Language
                              </label>
                              <select
                                className="form-control"
                                value={item.language}
                                onChange={(e) =>
                                  handleTitleTypeChange(
                                    index,
                                    "language",
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="">Select Language</option>
                                {language.map((lang) => (
                                  <option key={lang._id} value={lang.code}>
                                    {lang.name}
                                  </option>
                                ))}
                              </select>
                              {errors[`titles-${index}-language`] && (
                                <small className="text-danger">
                                  {errors[`titles-${index}-language`]}
                                </small>
                              )}
                            </Col>
                            <Col md="6" className="my-2">
                              <label
                                htmlFor={`title-${index}`}
                                className="form-label text-dark m-0 pb-1 fw-sbold text-uppercase"
                              >
                                Title
                              </label>
                              <div className="d-flex align-items-center">
                                <input
                                  type="text"
                                  className="form-control me-2"
                                  value={item.title}
                                  onChange={(e) =>
                                    handleTitleTypeChange(
                                      index,
                                      "title",
                                      e.target.value,
                                    )
                                  }
                                />
                                <Button
                                  variant="danger"
                                  className="btn-sm"
                                  onClick={() => removeTitleType(index)}
                                  disabled={formData.titles?.length === 1}
                                >
                                  Remove
                                </Button>
                              </div>
                              {errors[`titles-${index}-title`] && (
                                <small className="text-danger">
                                  {errors[`titles-${index}-title`]}
                                </small>
                              )}
                            </Col>
                          </React.Fragment>
                        ))}
                        <div className="d-flex px-4">
                          <Button
                            className="mt-2 btn-sm"
                            onClick={addTitleType}
                            type="button"
                          >
                            Add More Title
                          </Button>
                        </div>

                        {/* Options Section */}
                        <div className="mt-5">
                          <h6
                            className="fw-sbold text-uppercase mb-3"
                            style={{ color: "#2E3A59" }}
                          >
                            Options
                          </h6>
                          {formData?.options?.map((option, optionIndex) => (
                            <div
                              key={optionIndex}
                              className="border rounded p-3 mb-4"
                              style={{ backgroundColor: "#f8f9fa" }}
                            >
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6
                                  className="fw-sbold mb-0"
                                  style={{ color: "#495057" }}
                                >
                                  Option {optionIndex + 1}
                                </h6>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => removeOption(optionIndex)}
                                  disabled={formData.options.length === 1}
                                >
                                  Remove Option
                                </Button>
                              </div>

                              <Row>
                                {/* Option Name */}
                                <Col md="6" className="my-2">
                                  <label className="form-label text-dark m-0 pb-1 fw-sbold text-uppercase">
                                    Option Name
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={option.name}
                                    onChange={(e) =>
                                      handleOptionChange(
                                        optionIndex,
                                        "name",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Enter option name"
                                  />
                                  {errors[`options-${optionIndex}-name`] && (
                                    <small className="text-danger">
                                      {errors[`options-${optionIndex}-name`]}
                                    </small>
                                  )}
                                </Col>

                                {/* Audio — Voice type only */}
                                {formData.type === "Voice" && (
                                  <Col md="6" className="my-2">
                                    <label className="form-label text-dark m-0 pb-1 fw-sbold text-uppercase">
                                      Upload Audio
                                    </label>
                                    {optionAudioLoading[optionIndex] ? (
                                      <div className="d-flex align-items-center bg-light py-2 px-4 rounded">
                                        <span
                                          className="spinner-border text-primary spinner-border-sm"
                                          role="status"
                                          aria-hidden="true"
                                        ></span>
                                        <span className="ms-2 text-primary">
                                          Uploading...
                                        </span>
                                      </div>
                                    ) : (
                                      <>
                                        {option.audio &&
                                        typeof option.audio === "string" ? (
                                          <div className="d-flex align-items-center bg-light py-2 px-4 rounded position-relative">
                                            <audio
                                              controls
                                              className="rounded-4"
                                            >
                                              <source
                                                src={option.audio}
                                                type="audio/mp3"
                                              />
                                              Your browser does not support the
                                              audio element.
                                            </audio>
                                            <Button
                                              variant="danger"
                                              className="btn-sm border-0 p-0 fw-sbold d-flex align-items-center justify-content-center position-absolute rounded-circle"
                                              style={{
                                                top: 5,
                                                right: 5,
                                                fontSize: 8,
                                                height: 12,
                                                width: 12,
                                              }}
                                              onClick={() =>
                                                handleOptionChange(
                                                  optionIndex,
                                                  "audio",
                                                  "",
                                                )
                                              }
                                            >
                                              X
                                            </Button>
                                          </div>
                                        ) : (
                                          <input
                                            type="file"
                                            className="form-control"
                                            accept=".mp3"
                                            onChange={(e) =>
                                              handleOptionAudioFiles(
                                                optionIndex,
                                                e.target.files,
                                              )
                                            }
                                          />
                                        )}
                                      </>
                                    )}
                                    {errors[`options-${optionIndex}-audio`] && (
                                      <small className="text-danger">
                                        {errors[`options-${optionIndex}-audio`]}
                                      </small>
                                    )}
                                  </Col>
                                )}

                                {/* Image & Video — non-Voice types */}
                                {formData.type !== "Voice" && (
                                  <>
                                    {/* Option Image */}
                                    <Col md="6" className="my-2">
                                      <label className="form-label text-dark m-0 pb-1 fw-sbold text-uppercase">
                                        Option Image
                                      </label>
                                      {optionImageLoading[optionIndex] ? (
                                        <div className="d-flex align-items-center bg-light py-2 px-4 rounded">
                                          <span
                                            className="spinner-border text-primary spinner-border-sm"
                                            role="status"
                                            aria-hidden="true"
                                          ></span>
                                          <span className="ms-2 text-primary">
                                            Uploading...
                                          </span>
                                        </div>
                                      ) : option.image ? (
                                        <div className="d-flex align-items-center bg-light py-2 px-4 rounded position-relative">
                                          <img
                                            className="rounded"
                                            src={option.image}
                                            alt="Option"
                                            width={80}
                                            height={80}
                                          />
                                          <Button
                                            variant="danger"
                                            size="sm"
                                            className="position-absolute"
                                            style={{
                                              top: 5,
                                              right: 5,
                                              fontSize: 10,
                                              padding: "2px 6px",
                                            }}
                                            onClick={() =>
                                              handleOptionChange(
                                                optionIndex,
                                                "image",
                                                "",
                                              )
                                            }
                                          >
                                            ×
                                          </Button>
                                        </div>
                                      ) : (
                                        <input
                                          type="file"
                                          className="form-control"
                                          accept=".jpeg,.jpg,.png,.gif,.bmp,.webp"
                                          onChange={(e) =>
                                            handleOptionImageFiles(
                                              optionIndex,
                                              e.target.files,
                                            )
                                          }
                                        />
                                      )}
                                      {errors[
                                        `options-${optionIndex}-image`
                                      ] && (
                                        <small className="text-danger">
                                          {
                                            errors[
                                              `options-${optionIndex}-image`
                                            ]
                                          }
                                        </small>
                                      )}
                                    </Col>

                                    {/* Option Video */}
                                    <Col md="6" className="my-2">
                                      <label className="form-label text-dark m-0 pb-1 fw-sbold text-uppercase">
                                        Option Video
                                      </label>
                                      {optionVideoLoading[optionIndex] ? (
                                        <div className="d-flex align-items-center bg-light py-2 px-4 rounded">
                                          <span
                                            className="spinner-border text-primary spinner-border-sm"
                                            role="status"
                                            aria-hidden="true"
                                          ></span>
                                          <span className="ms-2 text-primary">
                                            Uploading...
                                          </span>
                                        </div>
                                      ) : option.video ? (
                                        <div className="d-flex align-items-center bg-light py-2 px-4 rounded position-relative">
                                          <video
                                            className="rounded"
                                            src={option.video}
                                            width={160}
                                            height={90}
                                            controls
                                          />
                                          <Button
                                            variant="danger"
                                            size="sm"
                                            className="position-absolute"
                                            style={{
                                              top: 5,
                                              right: 5,
                                              fontSize: 10,
                                              padding: "2px 6px",
                                            }}
                                            onClick={() =>
                                              handleOptionChange(
                                                optionIndex,
                                                "video",
                                                "",
                                              )
                                            }
                                          >
                                            ×
                                          </Button>
                                        </div>
                                      ) : (
                                        <input
                                          type="file"
                                          className="form-control"
                                          accept=".mp4,.webm,.ogg,.mov,.avi"
                                          onChange={(e) =>
                                            handleOptionVideoFiles(
                                              optionIndex,
                                              e.target.files,
                                            )
                                          }
                                        />
                                      )}
                                      {errors[
                                        `options-${optionIndex}-video`
                                      ] && (
                                        <small className="text-danger">
                                          {
                                            errors[
                                              `options-${optionIndex}-video`
                                            ]
                                          }
                                        </small>
                                      )}
                                    </Col>
                                  </>
                                )}

                                {/* ✅ NEW: Personality Traits — shown only when type is Personality */}
                                {formData.type === "Personality" && (
                                  <Col md="12" className="my-2">
                                    <PersonalityTraitsPanel
                                      optionIndex={optionIndex}
                                      optionName={option.name}
                                      traits={option.traits || []}
                                      onChange={(updatedTraits) =>
                                        handleOptionChange(
                                          optionIndex,
                                          "traits",
                                          updatedTraits,
                                        )
                                      }
                                    />
                                  </Col>
                                )}

                                {/* Option Titles */}
                                <Col md="12" className="my-3">
                                  <div className="border-top pt-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <label className="form-label text-dark m-0 pb-1 fw-sbold text-uppercase">
                                        Option Titles
                                      </label>
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() =>
                                          addOptionTitle(optionIndex)
                                        }
                                      >
                                        Add Title
                                      </Button>
                                    </div>

                                    {option.titles?.map((title, titleIndex) => (
                                      <Row key={titleIndex} className="mb-2">
                                        <Col md="5">
                                          <select
                                            className="form-control"
                                            value={title.language}
                                            onChange={(e) =>
                                              handleOptionTitleChange(
                                                optionIndex,
                                                titleIndex,
                                                "language",
                                                e.target.value,
                                              )
                                            }
                                          >
                                            <option value="">
                                              Select Language
                                            </option>
                                            {language.map((lang) => (
                                              <option
                                                key={lang._id}
                                                value={lang.code}
                                              >
                                                {lang.name}
                                              </option>
                                            ))}
                                          </select>
                                          {errors[
                                            `options-${optionIndex}-titles-${titleIndex}-language`
                                          ] && (
                                            <small className="text-danger">
                                              {
                                                errors[
                                                  `options-${optionIndex}-titles-${titleIndex}-language`
                                                ]
                                              }
                                            </small>
                                          )}
                                        </Col>
                                        <Col md="5">
                                          <input
                                            type="text"
                                            className="form-control"
                                            value={title.title}
                                            placeholder="Enter title"
                                            onChange={(e) =>
                                              handleOptionTitleChange(
                                                optionIndex,
                                                titleIndex,
                                                "title",
                                                e.target.value,
                                              )
                                            }
                                          />
                                          {errors[
                                            `options-${optionIndex}-titles-${titleIndex}-title`
                                          ] && (
                                            <small className="text-danger">
                                              {
                                                errors[
                                                  `options-${optionIndex}-titles-${titleIndex}-title`
                                                ]
                                              }
                                            </small>
                                          )}
                                        </Col>
                                        <Col md="2">
                                          <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() =>
                                              removeOptionTitle(
                                                optionIndex,
                                                titleIndex,
                                              )
                                            }
                                            disabled={
                                              option.titles.length === 1
                                            }
                                            className="w-100"
                                          >
                                            Remove
                                          </Button>
                                        </Col>
                                      </Row>
                                    ))}
                                  </div>
                                </Col>
                              </Row>
                            </div>
                          ))}

                          <div className="d-flex px-3">
                            <Button
                              variant="primary"
                              className="btn-sm"
                              onClick={addOption}
                              type="button"
                            >
                              Add More Option
                            </Button>
                          </div>
                        </div>
                      </Row>
                    </div>
                  </div>
                  <div className="py-3">
                    <div className="mx-auto" style={{ maxWidth: 777 }}>
                      <Row>
                        <Col md="5" className="my-2">
                          <Button
                            className="text-uppercase fw-sbold w-100 btn-danger"
                            onClick={() => navigate(-1)}
                          >
                            Cancel
                          </Button>
                        </Col>
                        <Col md="7" className="my-2">
                          <Button
                            disabled={isDisabled}
                            type="submit"
                            className="d-flex align-items-center justify-content-center commonBtn w-100 fw-sbold"
                          >
                            {isDisabled ? "Editing..." : "Edit"}
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default EditCharacterType;
